import { useState, useMemo } from 'react';
import { Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HistorialDistribuciones } from './utilidad/HistorialDistribuciones';
import { RepartirUtilidadModal } from './utilidad/RepartirUtilidadModal';
import { EvolucionRepartosChart } from './utilidad/EvolucionRepartosChart';
import { FondoTiposIncluidos } from './utilidad/FondoTiposIncluidos';
import { DateRangeFilter } from './shared/DateRangeFilter';
import { DateRangeSelector } from './shared/DateRangeSelector';
import { DistribucionChart } from './shared/DistribucionChart';
import { SectionCard } from './shared/SectionCard';
import { useKpis } from '../hooks/useKpis';
import { useFondos } from '../hooks/useFondos';
import { useUtilidad } from '../hooks/useUtilidad';
import { useSocios } from '../hooks/useSocios';
import { useCajas } from '../hooks/useCajas';
import { useDateRangeFilter, buildMonthRange } from '../hooks/useDateRangeFilter';
import { formatCurrency } from '../utils/formatters';
import { matchesDateRange } from '../utils/dateRange';
import { aggregateBySocio, buildRepartosMensuales, buildSocioColorMap } from '../utils/aggregations';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { FONDO_LABELS } from '../constants';
import type { FondoContable, Moneda } from '../types';
import { cn } from '@/lib/utils';

const FONDOS: FondoContable[] = ['escrituras', 'certificaciones_poderes'];

export function UtilidadPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [fondoReparto, setFondoReparto] = useState<FondoContable>('escrituras');
  const [monedaChart, setMonedaChart] = useState<Moneda>('ARS');
  const { canCreateContabilidad } = useAuthorization();

  const { range, activeMonth, selectMonth, selectRange, selectAll } = useDateRangeFilter(
    buildMonthRange(new Date().getMonth()),
  );

  const { kpis, isLoading: isLoadingKpis } = useKpis();
  const { fondos } = useFondos();
  const { historialUtilidad, confirmarReparto, isConfirmando, cancelarDistribucion, isCancelando } = useUtilidad();
  const { socios } = useSocios();
  const { cajas } = useCajas();

  const historialFiltrado = useMemo(
    () => historialUtilidad.filter((h) => matchesDateRange(h.fechaReparto, range)),
    [historialUtilidad, range],
  );

  const distribucionSocios = useMemo(
    () => aggregateBySocio(historialFiltrado, monedaChart),
    [historialFiltrado, monedaChart],
  );

  // La evolución mensual siempre muestra el año completo, sin importar el filtro de mes elegido arriba.
  const rangoAnioCompleto = useMemo(() => {
    const anio = new Date().getFullYear();
    return { start: new Date(anio, 0, 1), end: new Date(anio, 11, 31) };
  }, []);

  const historialAnual = useMemo(
    () => historialUtilidad.filter((h) => matchesDateRange(h.fechaReparto, rangoAnioCompleto)),
    [historialUtilidad, rangoAnioCompleto],
  );

  const { data: repartosARS, socios: sociosMensual } = useMemo(
    () => buildRepartosMensuales(historialAnual, 'ARS', rangoAnioCompleto),
    [historialAnual, rangoAnioCompleto],
  );
  const { data: repartosUSD } = useMemo(
    () => buildRepartosMensuales(historialAnual, 'USD', rangoAnioCompleto),
    [historialAnual, rangoAnioCompleto],
  );

  const socioColorMap = useMemo(() => buildSocioColorMap(sociosMensual), [sociosMensual]);
  const paletteSocios = distribucionSocios.map((slice) => socioColorMap[slice.name] ?? '#718096');

  if (isLoadingKpis || !kpis) {
    return <div className="p-4 text-sm text-muted-foreground">Cargando...</div>;
  }

  const disponiblePorFondo = Object.fromEntries(
    FONDOS.map((fondo) => [
      fondo,
      { ARS: kpis.ARS.disponiblePorFondo[fondo], USD: kpis.USD.disponiblePorFondo[fondo] },
    ]),
  ) as Record<FondoContable, { ARS: number; USD: number }>;

  const monedaToggle = (
    <div className="flex items-center gap-1 bg-gray-100 rounded-full px-1.5 py-1 border border-gray-200">
      {(['ARS', 'USD'] as Moneda[]).map((m) => (
        <button
          key={m}
          onClick={() => setMonedaChart(m)}
          className={cn(
            'px-3 py-0.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap',
            monedaChart === m
              ? 'bg-white text-primary shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {m}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <h2 className="text-xl font-semibold text-gray-900">Distribución de Utilidades</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangeFilter
            activeMonth={activeMonth}
            onSelectMonth={(m) => (m === 'todos' ? selectAll() : selectMonth(m as number))}
          />
          <DateRangeSelector value={range} onChange={(r) => (r ? selectRange(r) : selectAll())} />
        </div>
      </div>

      {/* Fila superior: Disponible/Repartir + Distribución por Socio */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-8">
          <div className="rounded-xl border-2 border-primary/30 bg-white p-6 shadow-sm h-full">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              Disponible para repartir
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FONDOS.map((fondo) => {
                const disponible = disponiblePorFondo[fondo];
                const arsNegativo = disponible.ARS <= 0;
                const usdNegativo = disponible.USD <= 0;
                const sinDisponible = arsNegativo && usdNegativo;

                return (
                  <div key={fondo} className="flex flex-col gap-4 rounded-lg border bg-slate-50 p-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-2">{FONDO_LABELS[fondo]}</p>
                      <p className={`text-2xl font-bold tabular-nums ${arsNegativo ? 'text-red-600' : 'text-emerald-600'}`}>
                        {formatCurrency(disponible.ARS, 'ARS')}
                      </p>
                      <p className={`text-xl font-bold tabular-nums mt-0.5 ${usdNegativo ? 'text-red-600' : 'text-emerald-600'}`}>
                        {formatCurrency(disponible.USD, 'USD')}
                      </p>
                    </div>

                    <FondoTiposIncluidos fondo={fondo} fondos={fondos} />

                    {canCreateContabilidad && (
                      <Button
                        className="bg-primary hover:bg-primary/90 gap-2 self-start mt-auto"
                        onClick={() => {
                          setFondoReparto(fondo);
                          setModalOpen(true);
                        }}
                        disabled={sinDisponible}
                        title={sinDisponible ? 'No hay utilidad disponible para repartir' : undefined}
                        aria-label={`Repartir utilidad de ${FONDO_LABELS[fondo]}`}
                      >
                        <Wallet className="size-4" />
                        Repartir Utilidad
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="xl:col-span-4">
          <SectionCard title="Distribución por Socio" action={monedaToggle} className="p-4 h-full flex flex-col">
            <DistribucionChart data={distribucionSocios} palette={paletteSocios} moneda={monedaChart} />
          </SectionCard>
        </div>
      </div>

      {/* Fila inferior: Evolución de Repartos por moneda */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Evolución de Repartos (USD)" className="p-5 h-full flex flex-col">
          <div className="flex-1 min-h-0">
            <EvolucionRepartosChart data={repartosUSD} socios={sociosMensual} colorMap={socioColorMap} moneda="USD" />
          </div>
        </SectionCard>
        <SectionCard title="Evolución de Repartos (ARS)" className="p-5 h-full flex flex-col">
          <div className="flex-1 min-h-0">
            <EvolucionRepartosChart data={repartosARS} socios={sociosMensual} colorMap={socioColorMap} moneda="ARS" />
          </div>
        </SectionCard>
      </div>

      {/* Historial de Distribuciones */}
      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <HistorialDistribuciones
          historial={historialFiltrado}
          onCancelar={cancelarDistribucion}
          isCancelando={isCancelando}
        />
      </div>

      {/* Modal Simulador de Reparto */}
      <RepartirUtilidadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fondo={fondoReparto}
        utilidadNeta={disponiblePorFondo[fondoReparto]}
        socios={socios}
        cajas={cajas}
        onConfirmar={confirmarReparto}
        isConfirmando={isConfirmando}
      />
    </div>
  );
}
